import React from 'react';
import { motion } from 'framer-motion';
import { X, Brain, Activity, Heart, Plus, ArrowRight } from 'lucide-react';

interface PlanSelectorProps {
  onClose: () => void;
}

const plans = [
  {
    id: 'custom',
    name: 'Custom Plan',
    description: 'Create your own custom plan',
    icon: <Plus className="w-6 h-6" />,
    color: 'from-primary to-purple-500'
  },
  {
    id: 'sleep',
    name: 'Sleep Priority',
    description: 'Focus on optimizing sleep quality and recovery',
    icon: <Brain className="w-6 h-6" />,
    color: 'from-purple-500 to-blue-500'
  },
  {
    id: 'strain',
    name: 'Strain Priority',
    description: 'Maximize performance and training load',
    icon: <Activity className="w-6 h-6" />,
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'recovery',
    name: 'Recovery Priority',
    description: 'Emphasize recovery and stress management',
    icon: <Heart className="w-6 h-6" />,
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'balance-strain-recovery',
    name: 'Balance Strain & Recovery',
    description: 'Optimal mix of performance and recovery',
    icon: <Activity className="w-6 h-6" />,
    color: 'from-orange-500 to-amber-500'
  },
  {
    id: 'balance-all',
    name: 'Balance All Metrics',
    description: 'Equal focus on strain, sleep, and recovery',
    icon: <Brain className="w-6 h-6" />,
    color: 'from-pink-500 to-rose-500'
  }
];

export function PlanSelector({ onClose }: PlanSelectorProps) {
  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-background rounded-xl border border-border max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
      >
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold">Select Plan</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4">
          {plans.map((plan) => (
            <motion.button
              key={plan.id}
              className="w-full p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors relative overflow-hidden group text-left"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${plan.color} opacity-5 group-hover:opacity-10 transition-opacity`} />
              <div className="relative flex items-center gap-4">
                <div className="relative">
                  {plan.icon}
                  <div className="absolute inset-0 bg-current opacity-20 blur-lg rounded-full" />
                </div>
                <div>
                  <h3 className="font-medium">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                </div>
                <ArrowRight className="w-5 h-5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </motion.button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}